import {DialogComponent} from '@theme/dialog';
import {trapFocus, removeTrapFocus} from '@theme/focus';

let privacyPromise;

function loadCustomerPrivacy() {
  if (typeof window.Shopify?.customerPrivacy?.currentVisitorConsent === 'function') {
    return Promise.resolve(window.Shopify.customerPrivacy);
  }
  if (privacyPromise) return privacyPromise;
  privacyPromise = new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => reject(new Error('Customer privacy load timed out')), 15000);
    if (typeof window.Shopify?.loadFeatures !== 'function') {
      window.clearTimeout(timeout);
      reject(new Error('Shopify customer privacy unavailable'));
      return;
    }
    window.Shopify.loadFeatures([{name:'consent-tracking-api', version:'0.1'}], error => {
      window.clearTimeout(timeout);
      if (error || !window.Shopify.customerPrivacy) {
        reject(error || new Error('Customer privacy unavailable'));
      } else {
        resolve(window.Shopify.customerPrivacy);
      }
    });
  }).catch(error => {
    privacyPromise = undefined;
    throw error;
  });
  return privacyPromise;
}

class LumenelleCookiePreferences extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    this.button = this.querySelector('.cookie-preferences__button');
    this.status = this.querySelector('.cookie-preferences__status');
    this.form = this.querySelector('form');
    this.button?.addEventListener('click', this.openPreferences);
    this.form?.addEventListener('submit', this.savePreferences);
    this.addEventListener('dialog:open', this.trapDialogFocus);
    this.addEventListener('dialog:close', this.restoreFocus);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.openPreferences);
    this.form?.removeEventListener('submit', this.savePreferences);
    this.removeEventListener('dialog:open', this.trapDialogFocus);
    this.removeEventListener('dialog:close', this.restoreFocus);
    if (this.refs.dialog?.open) removeTrapFocus();
    super.disconnectedCallback();
  }

  openPreferences = async () => {
    if (!this.button || this.button.disabled) return;
    this.button.disabled = true;
    this.button.setAttribute('aria-busy', 'true');
    this.status.hidden = true;
    try {
      const privacy = await loadCustomerPrivacy();
      const consent = privacy.currentVisitorConsent();
      for (const input of this.form.querySelectorAll('input[name]')) {
        input.checked = consent[input.name] === 'yes';
      }
      this.form.querySelector('[data-save-status]').hidden = true;
      this.showDialog();
    } catch {
      this.status.textContent = this.button.dataset.errorMessage;
      this.status.hidden = false;
    } finally {
      this.button.disabled = false;
      this.button.removeAttribute('aria-busy');
    }
  };

  trapDialogFocus = () => {
    trapFocus(this.refs.dialog);
    this.refs.dialog.querySelector('button')?.focus();
  };

  restoreFocus = () => {
    removeTrapFocus();
    this.button?.focus({preventScroll:true});
  };

  savePreferences = async event => {
    event.preventDefault();
    const button = this.form.querySelector('[type="submit"]');
    const status = this.form.querySelector('[data-save-status]');
    if (button.disabled) return;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    status.hidden = true;
    try {
      const privacy = await loadCustomerPrivacy();
      const preferences = Object.fromEntries(Array.from(this.form.querySelectorAll('input[name]'), input => [input.name, input.checked]));
      await new Promise((resolve, reject) => {
        const timeout = window.setTimeout(() => reject(new Error('Consent save timed out')), 15000);
        privacy.setTrackingConsent(preferences, result => {
          window.clearTimeout(timeout);
          if (result?.error) reject(new Error('Consent save failed'));
          else resolve();
        });
      });
      this.closeDialog();
    } catch {
      status.textContent = this.form.dataset.saveError;
      status.hidden = false;
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('lumenelle-cookie-preferences')) {
  customElements.define('lumenelle-cookie-preferences', LumenelleCookiePreferences);
}
